import React, { useState, useEffect } from 'react';
import { PlacedBlock, BlockSystem, PartAttribute, PipeType, SocketConnection, PlacedLabel, TextAnnotation } from '@/app/types/cad';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/app/components/ui/tabs';
import { ScrollArea } from '@/app/components/ui/scroll-area';
import { Trash2, Plus, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { LabelProperties } from '@/app/components/LabelProperties';
import { useLanguage } from '@/app/contexts/LanguageContext';

interface SystemPropertiesPanelProps {
  selectedBlocks: PlacedBlock[];
  selectedSystem: BlockSystem | null;
  selectedConnections?: SocketConnection[];
  selectedLabels?: PlacedLabel[];
  allBlocks?: PlacedBlock[];
  allConnections?: SocketConnection[];
  allTextAnnotations?: TextAnnotation[];
  allSystems: BlockSystem[];
  pipeTypes: PipeType[];
  onBlockUpdate: (id: string, updates: Partial<PlacedBlock>) => void;
  onSystemUpdate: (id: string, updates: Partial<BlockSystem>) => void;
  onSystemAttributeAdd: (systemId: string, attribute: Omit<PartAttribute, 'id'>) => void;
  onSystemAttributeUpdate: (systemId: string, attrId: string, updates: Partial<PartAttribute>) => void;
  onSystemAttributeDelete: (systemId: string, attrId: string) => void;
  onConnectionAttributeAdd?: (connectionId: string, attribute: Omit<PartAttribute, 'id'>) => void;
  onConnectionAttributeUpdate?: (connectionId: string, attrId: string, updates: Partial<PartAttribute>) => void;
  onConnectionAttributeDelete?: (connectionId: string, attrId: string) => void;
  onSystemSelect?: (systemId: string) => void;
  onLabelEdit?: (labelId: string) => void;
  onLabelReassign?: (labelId: string) => void; // NEW: Callback for reassigning orphaned labels
}

export function SystemPropertiesPanel({
  selectedBlocks,
  selectedSystem,
  selectedConnections,
  selectedLabels,
  allBlocks,
  allConnections,
  allTextAnnotations,
  allSystems,
  pipeTypes,
  onBlockUpdate,
  onSystemUpdate,
  onSystemAttributeAdd,
  onSystemAttributeUpdate,
  onSystemAttributeDelete,
  onConnectionAttributeAdd,
  onConnectionAttributeUpdate,
  onConnectionAttributeDelete,
  onSystemSelect,
  onLabelEdit,
  onLabelReassign,
}: SystemPropertiesPanelProps) {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<'block' | 'system' | 'connection' | 'label'>('block');
  const [expandedSystemId, setExpandedSystemId] = useState<string | null>(null);

  const hasBlockSelection = selectedBlocks.length > 0;
  const hasConnectionSelection = (selectedConnections && selectedConnections.length > 0) || false;
  const hasLabelSelection = (selectedLabels && selectedLabels.length > 0) || false;
  const hasSystems = allSystems.length > 0;

  // Calculate visible tabs
  const visibleTabs = [
    hasBlockSelection && 'block',
    hasConnectionSelection && 'connection',
    hasLabelSelection && 'label',
    hasSystems && 'system',
  ].filter(Boolean) as ('block' | 'connection' | 'label' | 'system')[];

  // Auto-switch to first available tab if current is not visible
  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0]);
    }
  }, [visibleTabs, activeTab]);

  // Auto-switch based on selection priority
  useEffect(() => {
    if (hasLabelSelection && activeTab !== 'label') {
      setActiveTab('label');
    } else if (hasConnectionSelection && activeTab !== 'connection') {
      setActiveTab('connection');
    } else if (hasBlockSelection && activeTab !== 'block') {
      setActiveTab('block');
    }
  }, [hasConnectionSelection, hasLabelSelection, hasBlockSelection]);

  return (
    <div
      className="w-80 border-l flex flex-col"
      style={{
        backgroundColor: 'var(--md-surface-container-low)',
        borderColor: 'var(--md-outline-variant)',
      }}
    >
      {visibleTabs.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full p-8 text-center">
          <p className="text-sm font-medium" style={{ color: 'var(--md-on-surface-variant)' }}>
            {t.editor.properties.noSelection}
          </p>
          <p className="text-xs mt-2" style={{ color: 'var(--md-on-surface-variant)' }}>
            {t.editor.properties.selectToEdit}
          </p>
        </div>
      ) : (
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'block' | 'system' | 'connection' | 'label')} className="flex flex-col h-full">
          <div className="border-b" style={{ borderColor: 'var(--md-outline-variant)' }}>
            <TabsList className="w-full" style={{ gridTemplateColumns: `repeat(${visibleTabs.length}, 1fr)`, display: 'grid' }}>
              {hasBlockSelection && (
                <TabsTrigger value="block">Block</TabsTrigger>
              )}
              {hasConnectionSelection && (
                <TabsTrigger value="connection">
                  {t.editor.tools.connection} ({selectedConnections?.length || 0})
                </TabsTrigger>
              )}
              {hasLabelSelection && (
                <TabsTrigger value="label">
                  Label ({selectedLabels?.length || 0})
                </TabsTrigger>
              )}
              {hasSystems && (
                <TabsTrigger value="system">
                  {t.editor.properties.systems} ({allSystems.length})
                </TabsTrigger>
              )}
            </TabsList>
          </div>

          <TabsContent value="block" className="flex-1 m-0 overflow-hidden">
            {hasBlockSelection ? (
              <BlockProperties blocks={selectedBlocks} pipeTypes={pipeTypes} onBlockUpdate={onBlockUpdate} />
            ) : (
              <EmptyState message={t.editor.properties.emptyBlockSelection} />
            )}
          </TabsContent>

          <TabsContent value="connection" className="flex-1 m-0 overflow-hidden">
            {hasConnectionSelection && onConnectionAttributeAdd && onConnectionAttributeUpdate && onConnectionAttributeDelete ? (
              <ConnectionProperties
                connections={selectedConnections || []}
                onAttributeAdd={onConnectionAttributeAdd}
                onAttributeUpdate={onConnectionAttributeUpdate}
                onAttributeDelete={onConnectionAttributeDelete}
              />
            ) : (
              <EmptyState message={t.editor.properties.emptyConnectionSelection} subtitle={t.editor.properties.clickConnectionToView} />
            )}
          </TabsContent>

          <TabsContent value="label" className="flex-1 m-0 overflow-hidden">
            {hasLabelSelection && selectedLabels && allBlocks && allConnections && allTextAnnotations ? (
              <LabelProperties
                labels={selectedLabels}
                allBlocks={allBlocks}
                allConnections={allConnections}
                allTextAnnotations={allTextAnnotations}
                onLabelEdit={onLabelEdit}
                onLabelReassign={onLabelReassign}
              />
            ) : (
              <EmptyState message={t.editor.properties.noLabelSelected} subtitle={t.editor.properties.clickLabelToView} />
            )}
          </TabsContent>

          <TabsContent value="system" className="flex-1 m-0 overflow-hidden">
            {allSystems.length > 0 ? (
              <SystemsList
                systems={allSystems}
                selectedSystem={selectedSystem}
                expandedSystemId={expandedSystemId}
                onSystemUpdate={onSystemUpdate}
                onAttributeAdd={onSystemAttributeAdd}
                onAttributeUpdate={onSystemAttributeUpdate}
                onAttributeDelete={onSystemAttributeDelete}
                onSystemExpand={setExpandedSystemId}
                onSystemSelect={onSystemSelect}
              />
            ) : (
              <EmptyState message={t.editor.properties.noSystems} subtitle={t.editor.properties.connectToCreateSystems} />
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

// Block Properties Component
function BlockProperties({
  blocks,
  pipeTypes,
  onBlockUpdate,
}: {
  blocks: PlacedBlock[];
  pipeTypes: PipeType[];
  onBlockUpdate: (id: string, updates: Partial<PlacedBlock>) => void;
}) {
  const { t } = useLanguage();
  const singleBlock = blocks.length === 1 ? blocks[0] : null;

  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-4">
        {/* Selection info */}
        <div className="pb-3 border-b" style={{ borderColor: 'var(--md-outline-variant)' }}>
          <h3 className="font-semibold text-sm">
            {blocks.length === 1 ? t.editor.properties.blockProperties : t.editor.properties.blocksSelected.replace('{count}', blocks.length.toString())}
          </h3>
        </div>

        {singleBlock && (
          <>
            {/* Block name */}
            <div className="space-y-2">
              <Label className="text-xs">{t.attributes.name}</Label>
              <Input
                value={singleBlock.blockData.name}
                disabled
                className="text-sm"
              />
            </div>

            {/* Pipe Type Selector - only show for pipes */}
            {singleBlock.blockData.name?.includes('Leitung') && (
              <div className="space-y-2 pb-3 border-b" style={{ borderColor: 'var(--md-outline-variant)' }}>
                <Label className="text-xs font-semibold">Leitungstyp ändern</Label>
                <select
                  className="w-full px-3 py-2 rounded border text-sm"
                  style={{
                    backgroundColor: 'var(--md-surface-container)',
                    borderColor: 'var(--md-outline)',
                    color: 'var(--md-on-surface)',
                  }}
                  onChange={(e) => {
                    const selectedType = pipeTypes.find(t => t.id === e.target.value);
                    if (!selectedType) return;

                    // Update pipe with new type
                    const updatedElements = singleBlock.blockData.elements.map(el => {
                      if (el.type === 'line') {
                        return {
                          ...el,
                          color: selectedType.color,
                          width: selectedType.lineWidth,
                          lineStyle: selectedType.lineStyle,
                        };
                      } else if (el.type === 'connection') {
                        return {
                          ...el,
                          color: selectedType.color,
                        };
                      }
                      return el;
                    });

                    // Update DN attribute
                    const updatedAttributes = singleBlock.blockData.attributes?.map(attr => {
                      if (attr.name === 'DN') {
                        return {
                          ...attr,
                          value: selectedType.dn,
                        };
                      }
                      return attr;
                    });

                    onBlockUpdate(singleBlock.id, {
                      blockData: {
                        ...singleBlock.blockData,
                        name: `Leitung DN${selectedType.dn}`,
                        elements: updatedElements,
                        attributes: updatedAttributes,
                      },
                    });

                    toast.success(`Leitungstyp geändert zu ${selectedType.name}`);
                  }}
                >
                  <option value="">Typ auswählen...</option>
                  {pipeTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs" style={{ color: 'var(--md-on-surface-variant)' }}>
                  Ändert DN, Farbe, Linienbreite und Linienart
                </p>
              </div>
            )}

            {/* Line Style Editor for line, circle, spline, arc elements */}
            {singleBlock.blockData.elements.some(el => 
              el.type === 'line' || el.type === 'circle' || el.type === 'spline' || el.type === 'arc'
            ) && (
              <div className="space-y-2 pb-3 border-b" style={{ borderColor: 'var(--md-outline-variant)' }}>
                <Label className="text-xs font-semibold">Linienart ändern</Label>
                <select
                  className="w-full px-3 py-2 rounded border text-sm"
                  style={{
                    backgroundColor: 'var(--md-surface-container)',
                    borderColor: 'var(--md-outline)',
                    color: 'var(--md-on-surface)',
                  }}
                  onChange={(e) => {
                    const newLineStyle = e.target.value as 'solid' | 'dashed' | 'dotted' | 'dashdot';
                    
                    // Update all line, circle, spline, arc elements
                    const updatedElements = singleBlock.blockData.elements.map(el => {
                      if (el.type === 'line' || el.type === 'circle' || el.type === 'spline' || el.type === 'arc') {
                        return {
                          ...el,
                          lineStyle: newLineStyle,
                        };
                      }
                      return el;
                    });

                    onBlockUpdate(singleBlock.id, {
                      blockData: {
                        ...singleBlock.blockData,
                        elements: updatedElements,
                      },
                    });

                    toast.success(`Linienart geändert zu ${
                      newLineStyle === 'solid' ? 'Durchgehend' :
                      newLineStyle === 'dashed' ? 'Gestrichelt' :
                      newLineStyle === 'dotted' ? 'Punktiert' :
                      'Strichpunktiert'
                    }`);
                  }}
                >
                  <option value="">Linienart wählen...</option>
                  <option value="solid">━━━ Durchgehend</option>
                  <option value="dashed">╌╌╌ Gestrichelt</option>
                  <option value="dotted">┄┄┄ Punktiert</option>
                  <option value="dashdot">╌·╌ Strichpunktiert</option>
                </select>
                <p className="text-xs" style={{ color: 'var(--md-on-surface-variant)' }}>
                  Ändert die Linienart aller Linien, Kreise, Splines und Bögen im Block
                </p>
              </div>
            )}

            {/* Block info */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold">{t.editor.properties.connectionInfo}</Label>
              <div className="text-xs space-y-1" style={{ color: 'var(--md-on-surface-variant)' }}>
                <div>Elemente: {singleBlock.blockData.elements.length}</div>
                <div>
                  Sockets:{' '}
                  {singleBlock.blockData.elements.filter((el) => el.type === 'connection').length}
                </div>
                {singleBlock.blockData.attributes && singleBlock.blockData.attributes.length > 0 && (
                  <div>Attribute: {singleBlock.blockData.attributes.length}</div>
                )}
              </div>
            </div>

            {/* Block Attributes (read-only) */}
            {singleBlock.blockData.attributes && singleBlock.blockData.attributes.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold">Block-Attribute</Label>
                <div className="space-y-1">
                  {singleBlock.blockData.attributes.map((attr) => (
                    <div
                      key={attr.id}
                      className="p-2 rounded text-xs"
                      style={{ backgroundColor: 'var(--md-surface-variant)' }}
                    >
                      <div className="font-medium">{attr.name}</div>
                      <div style={{ color: 'var(--md-on-surface-variant)' }}>
                        {String(attr.value)} {attr.unit}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {blocks.length > 1 && (
          <div className="text-sm" style={{ color: 'var(--md-on-surface-variant)' }}>
            {t.editor.properties.multiSelectionWarning}
          </div>
        )}
      </div>
    </ScrollArea>
  );
}

// Connection Properties Component
function ConnectionProperties({
  connections,
  onAttributeAdd,
  onAttributeUpdate,
  onAttributeDelete,
}: {
  connections: SocketConnection[];
  onAttributeAdd: (connectionId: string, attribute: Omit<PartAttribute, 'id'>) => void;
  onAttributeUpdate: (connectionId: string, attrId: string, updates: Partial<PartAttribute>) => void;
  onAttributeDelete: (connectionId: string, attrId: string) => void;
}) {
  const { t } = useLanguage();
  const singleConnection = connections.length === 1 ? connections[0] : null;
  
  // Debug logging
  React.useEffect(() => {
    if (singleConnection) {
      console.log('🔍 ConnectionProperties - Single connection:', {
        id: singleConnection.id,
        connectedSockets: singleConnection.connectedSockets,
        systemId: singleConnection.systemId,
        dn: singleConnection.dn,
        position: singleConnection.position
      });
    }
  }, [singleConnection]);

  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-4">
        {/* Selection info */}
        <div className="pb-3 border-b" style={{ borderColor: 'var(--md-outline-variant)' }}>
          <h3 className="font-semibold text-sm">
            {connections.length === 1 ? t.editor.properties.connectionProperties : t.editor.properties.connectionsSelected.replace('{count}', connections.length.toString())}
          </h3>
        </div>

        {singleConnection && (
          <>
            {/* Connection info */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold">{t.editor.properties.connectionInfo}</Label>
              <div className="text-xs space-y-1" style={{ color: 'var(--md-on-surface-variant)' }}>
                <div>
                  {t.editor.properties.connectedComponents}: {singleConnection.connectedSockets.length}
                  {singleConnection.connectedSockets.length > 2 && (
                    <span className="ml-1 text-amber-600">({t.editor.properties.multiWay})</span>
                  )}
                </div>
                <div className="text-[10px] opacity-70">
                  {singleConnection.connectedSockets.length === 2 
                    ? t.editor.properties.standardConnection
                    : t.editor.properties.multiWayConnection.replace('{count}', singleConnection.connectedSockets.length.toString())
                  }
                </div>
                {singleConnection.dn && <div>DN: {singleConnection.dn}</div>}
                {singleConnection.systemId && <div>System-ID: {singleConnection.systemId}</div>}
              </div>
            </div>

            {/* Connection Attributes */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold">{t.editor.properties.connectionAttributes}</Label>

              {/* Existing attributes */}
              {singleConnection.attributes && singleConnection.attributes.length > 0 && (
                <div className="space-y-2 mb-3">
                  {singleConnection.attributes.map((attr) => (
                    <div
                      key={attr.id}
                      className="p-2 rounded flex items-start gap-2"
                      style={{ backgroundColor: 'var(--md-surface-variant)' }}
                    >
                      <div className="flex-1 min-w-0">
                        <Input
                          value={attr.name}
                          onChange={(e) => {
                            onAttributeUpdate(singleConnection.id, attr.id, { name: e.target.value });
                          }}
                          className="text-xs font-medium mb-1"
                          placeholder={t.attributes.name}
                          disabled={attr.readonly}
                        />
                        <Input
                          value={String(attr.value)}
                          onChange={(e) => {
                            onAttributeUpdate(singleConnection.id, attr.id, { value: e.target.value });
                          }}
                          className="text-xs"
                          placeholder={t.attributes.value}
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onAttributeDelete(singleConnection.id, attr.id)}
                        className="flex-shrink-0"
                        disabled={attr.readonly}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add new attribute button */}
              <Button
                onClick={() => {
                  onAttributeAdd(singleConnection.id, {
                    name: 'Neues Attribut',
                    type: 'string',
                    value: '',
                  });
                }}
                size="sm"
                className="w-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t.editor.properties.addAttribute}
              </Button>

              {/* Hint for attribute copy/paste */}
              <div className="text-xs p-2 rounded" style={{ 
                backgroundColor: 'var(--md-surface-container-highest)',
                color: 'var(--md-on-surface-variant)' 
              }}>
                💡 <strong>Tipp:</strong> {t.editor.properties.copyPasteTip.replace('Tipp: ', '')}
              </div>
            </div>

            {/* Divider */}
            <div className="border-t pt-2" style={{ borderColor: 'var(--md-outline-variant)' }} />
          </>
        )}

        {connections.length > 1 && (
          <div className="text-sm" style={{ color: 'var(--md-on-surface-variant)' }}>
            {t.editor.properties.multiSelectionWarning}
          </div>
        )}
      </div>
    </ScrollArea>
  );
}

// Systems List Component
function SystemsList({
  systems,
  selectedSystem,
  expandedSystemId,
  onSystemUpdate,
  onAttributeAdd,
  onAttributeUpdate,
  onAttributeDelete,
  onSystemExpand,
  onSystemSelect,
}: {
  systems: BlockSystem[];
  selectedSystem: BlockSystem | null;
  expandedSystemId: string | null;
  onSystemUpdate: (id: string, updates: Partial<BlockSystem>) => void;
  onAttributeAdd: (systemId: string, attribute: Omit<PartAttribute, 'id'>) => void;
  onAttributeUpdate: (systemId: string, attrId: string, updates: Partial<PartAttribute>) => void;
  onAttributeDelete: (systemId: string, attrId: string) => void;
  onSystemExpand: (systemId: string | null) => void;
  onSystemSelect?: (systemId: string) => void;
}) {
  const { t } = useLanguage();

  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-4">
        {/* System info */}
        <div className="pb-3 border-b" style={{ borderColor: 'var(--md-outline-variant)' }}>
          <h3 className="font-semibold text-sm">{t.editor.properties.systems}</h3>
        </div>

        {systems.map((system) => (
          <div key={system.id} className="space-y-2">
            {/* System name */}
            <div className="space-y-2">
              <Label className="text-xs">{t.attributes.name}</Label>
              <Input
                value={system.name}
                onChange={(e) => {
                  onSystemUpdate(system.id, { name: e.target.value });
                }}
                className="text-sm"
                placeholder="System-Name"
              />
            </div>

            {/* System color */}
            <div className="space-y-2">
              <Label className="text-xs">{t.editor.properties.color}</Label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={system.color}
                  onChange={(e) => {
                    onSystemUpdate(system.id, { color: e.target.value });
                  }}
                  className="w-16 h-10"
                />
                <Input
                  value={system.color}
                  onChange={(e) => {
                    onSystemUpdate(system.id, { color: e.target.value });
                  }}
                  className="text-sm flex-1"
                />
              </div>
            </div>

            {/* System description */}
            <div className="space-y-2">
              <Label className="text-xs">{t.attributes.description}</Label>
              <Input
                value={system.description || ''}
                onChange={(e) => {
                  onSystemUpdate(system.id, { description: e.target.value });
                }}
                className="text-sm"
                placeholder="Optionale Beschreibung"
              />
            </div>

            {/* System stats */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold">Statistik</Label>
              <div className="text-xs space-y-1" style={{ color: 'var(--md-on-surface-variant)' }}>
                <div>{t.feedback.context.blocks}: {system.blockIds.length}</div>
                <div>{t.feedback.context.connections}: {system.connectionIds.length}</div>
              </div>
            </div>

            {/* System Attributes */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold">{t.editor.properties.systemAttributes}</Label>

              {/* Existing attributes */}
              {system.attributes && system.attributes.length > 0 && (
                <div className="space-y-2 mb-3">
                  {system.attributes.map((attr) => (
                    <div
                      key={attr.id}
                      className="p-2 rounded flex items-start gap-2"
                      style={{ backgroundColor: 'var(--md-surface-variant)' }}
                    >
                      <div className="flex-1 min-w-0">
                        <Input
                          value={attr.name}
                          onChange={(e) => {
                            onAttributeUpdate(system.id, attr.id, { name: e.target.value });
                          }}
                          className="text-xs font-medium mb-1"
                          placeholder="Name"
                        />
                        <Input
                          value={String(attr.value)}
                          onChange={(e) => {
                            onAttributeUpdate(system.id, attr.id, { value: e.target.value });
                          }}
                          className="text-xs"
                          placeholder="Wert"
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onAttributeDelete(system.id, attr.id)}
                        className="flex-shrink-0"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add new attribute button */}
              <Button
                onClick={() => {
                  onAttributeAdd(system.id, {
                    name: 'Neues Attribut',
                    type: 'string',
                    value: '',
                  });
                }}
                size="sm"
                className="w-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Attribut hinzufügen
              </Button>
            </div>

            {/* Divider */}
            <div className="border-t pt-2" style={{ borderColor: 'var(--md-outline-variant)' }} />
          </div>
        ))}
      </div>
    </ScrollArea>
  );
}

// Empty state component
function EmptyState({ message, subtitle }: { message: string; subtitle?: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-full p-4 text-center">
      <p className="text-sm" style={{ color: 'var(--md-on-surface-variant)' }}>
        {message}
      </p>
      {subtitle && (
        <p className="text-xs mt-2" style={{ color: 'var(--md-on-surface-variant)' }}>
          {subtitle}
        </p>
      )}
    </div>
  );
}